import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Tabs,
  Modal,
  List,
  Tooltip,
  Badge,
  Divider
} from 'antd';
import {
  MonitorOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  HddOutlined,
  CloudServerOutlined,
  BugOutlined,
  InfoCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Monitoreo = () => {
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metricas, setMetricas] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [basesDatos, setBasesDatos] = useState([]);
  const [logs, setLogs] = useState([]);
  const [healthCheck, setHealthCheck] = useState(null);
  
  // Estados de modales
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  
  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Detectar móvil
  const isMobile = window.innerWidth <= 768;

  // Cargar datos principales
  const cargarDatos = async () => {
    try {
      const token = localStorage.getItem('horeb_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Cargar métricas del servidor
      const metricasResponse = await axios.get('/api/sistema/metricas', { headers });
      if (metricasResponse.data.success) {
        setMetricas(metricasResponse.data.data);
      }

      // Cargar servicios PM2
      const serviciosResponse = await axios.get('/api/sistema/servicios', { headers });
      if (serviciosResponse.data.success) {
        setServicios(serviciosResponse.data.data.servicios);
      }

      // Cargar estado de bases de datos
      const bdsResponse = await axios.get('/api/sistema/bases-datos', { headers });
      if (bdsResponse.data.success) {
        setBasesDatos(bdsResponse.data.data.bases_datos);
      }

      // Health check

            const healthResponse = await axios.get('/api/sistema/health', { headers });
            if (healthResponse.data.success) {
            setHealthCheck(healthResponse.data.data);
            }

            // Cargar logs generales del sistema
            const logsResponse = await axios.get('/api/sistema/logs', { 
            headers,
            params: { lineas: 20 }
            });
            if (logsResponse.data.success) {
            setLogs(logsResponse.data.data.logs);
            }

    } catch (error) {
      console.error('Error cargando datos de monitoreo:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Refrescar datos manualmente
  const handleRefresh = () => {
    setRefreshing(true);
    cargarDatos();
  };

  // Reiniciar servicio
const reiniciarServicio = async (nombreServicio) => {
    const modal = Modal.confirm({
      title: '¿Reiniciar servicio?',
      content: `¿Estás seguro de reiniciar ${nombreServicio}?`,
      onOk: async () => {
        try {
          modal.update({
            content: `Reiniciando ${nombreServicio}...`,
            okButtonProps: { loading: true }
          });

          const token = localStorage.getItem('horeb_admin_token');
          const response = await axios.post(`/api/sistema/servicios/${nombreServicio}/reiniciar`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            Modal.success({
              title: 'Servicio Reiniciado',
              content: `${nombreServicio} ha sido reiniciado exitosamente`
            });
            
            setTimeout(() => {
              handleRefresh();
            }, 2000);
          }
          
        } catch (error) {
          console.error('Error reiniciando servicio:', error);
          Modal.error({
            title: 'Error al Reiniciar',
            content: `No se pudo reiniciar ${nombreServicio}. ${error.response?.data?.mensaje || error.message}`
          });
        }
      }
    });
  };

  // Ver logs de un servicio
  const verLogs = async (servicio) => {
    try {
      const token = localStorage.getItem('horeb_admin_token');
      const response = await axios.get('/api/sistema/logs', {
        headers: { Authorization: `Bearer ${token}` },
        params: { servicio: servicio.name, lineas: 50 }
      });
      
      if (response.data.success) {
        setLogs(response.data.data.logs);
        setServicioSeleccionado(servicio);
        setLogsModalVisible(true);
      }
    } catch (error) {
      console.error('Error obteniendo logs:', error);
    }
  };

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    cargarDatos();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        cargarDatos();
      }, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Columnas para tabla de servicios PM2
  const columnasServicios = [
    {
      title: 'Servicio',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            PID: {record.pid || 'N/A'}
          </Text>
        </Space>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'online' ? 'green' : 
                     status === 'stopped' ? 'red' : 'orange';
        const icon = status === 'online' ? <CheckCircleOutlined /> :
                     status === 'stopped' ? <StopOutlined /> : <ExclamationCircleOutlined />;
        
        return (
          <Tag color={color} icon={icon}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Uptime',
      dataIndex: 'uptime',
      key: 'uptime',
      render: (uptime) => (
        <Space>
          <ClockCircleOutlined />
          <Text>{uptime}</Text>
        </Space>
      )
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      render: (cpu) => <Text>{cpu}</Text>
    },
    {
      title: 'Memoria',
      dataIndex: 'memoria',
      key: 'memoria',
      render: (memoria) => <Text>{memoria}</Text>
    },
    {
      title: 'Reintentos',
      dataIndex: 'reintentos',
      key: 'reintentos',
      render: (reintentos) => (
  <Tooltip title={`Número de veces que PM2 ha reiniciado automáticamente este servicio. 0 = muy estable, >5 = problemas`}>
    <Badge 
      count={reintentos} 
      style={{ 
        backgroundColor: reintentos === 0 ? '#52c41a' : 
                         reintentos < 3 ? '#faad14' : '#f50' 
      }} 
    />
  </Tooltip>
)
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver logs">
            <Button 
              size="small" 
              icon={<BugOutlined />} 
              onClick={() => verLogs(record)}
            />
          </Tooltip>
          <Tooltip title="Reiniciar servicio">
            <Button 
              size="small" 
              icon={<ReloadOutlined />} 
              onClick={() => reiniciarServicio(record.name)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Columnas para tabla de bases de datos
  const columnasBD = [
    {
      title: 'Base de Datos',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (nombre, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{nombre}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.conjunto}
          </Text>
        </Space>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      render: (tipo) => (
        <Tag color={tipo === 'central' ? 'blue' : 'green'}>
          {tipo === 'central' ? 'CENTRAL' : 'CONJUNTO'}
        </Tag>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={estado === 'conectada' ? 'green' : 'red'}>
          {estado === 'conectada' ? 'CONECTADA' : 'ERROR'}
        </Tag>
      )
    },
    {
      title: 'Conexiones',
      dataIndex: 'conexiones_activas',
      key: 'conexiones_activas',
      render: (conexiones) => (
        <Badge count={conexiones} style={{ backgroundColor: '#52c41a' }} />
      )
    },
    {
      title: 'Tamaño',
      dataIndex: 'tamaño_mb',
      key: 'tamaño_mb',
      render: (tamaño) => <Text>{tamaño} MB</Text>
    }
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <Title level={2} style={{ margin: 0 }}>
          <MonitorOutlined /> Monitoreo del Sistema
        </Title>
        
        <Space>
          <Button 
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refrescar
          </Button>
          <Button 
            type={autoRefresh ? 'primary' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </Space>
      </div>

      {/* Health Check Alert */}
      {healthCheck && healthCheck.sistema !== 'online' && (
        <Alert
          message="Sistema en Estado Degradado"
          description="Algunos componentes del sistema presentan problemas"
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Tabs defaultActiveKey="overview" size={isMobile ? 'small' : 'default'}>
        {/* Tab de Resumen General */}
        <TabPane tab="Resumen" key="overview">
          <Row gutter={[16, 16]}>
            {/* Métricas del Servidor */}
            <Col xs={24} lg={12}>
              <Card 
                title={<><CloudOutlined /> Servidor</>}
                extra={<Tag color="green">ONLINE</Tag>}
              >
                {metricas && (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>CPU</Text>
                      <Progress 
                        percent={parseFloat(metricas.cpu.porcentaje_uso)} 
                        status={parseFloat(metricas.cpu.porcentaje_uso) > 80 ? 'exception' : 'normal'}
                        format={percent => `${percent}%`}
                      />
                      <Text type="secondary">
                        Load: {metricas.cpu.load_1min} | Cores: {metricas.cpu.cores}
                      </Text>
                    </div>
                    
                    <div>
                      <Text strong>Memoria</Text>
                      <Progress 
                        percent={parseFloat(metricas.memoria.porcentaje_uso)} 
                        status={parseFloat(metricas.memoria.porcentaje_uso) > 80 ? 'exception' : 'normal'}
                        format={percent => `${percent}%`}
                      />
                      <Text type="secondary">
                        {metricas.memoria.usado_gb}GB / {metricas.memoria.total_gb}GB
                      </Text>
                    </div>

                   <div>
                        <Text strong>Disco</Text>
                        <Progress 
                            percent={metricas.disco?.porcentaje ? parseFloat(metricas.disco.porcentaje.replace('%', '')) : 0} 
                            status={metricas.disco?.porcentaje && parseFloat(metricas.disco.porcentaje.replace('%', '')) > 85 ? 'exception' : 'normal'}
                            format={percent => `${percent}%`}
                        />
                        <Text type="secondary">
                            {metricas.disco?.used || 'N/A'} usado de {metricas.disco?.total || 'N/A'} total
                        </Text>
                        </div>

                    <Divider />
                    
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Uptime"
                          value={metricas.sistema.uptime_formateado}
                          prefix={<ClockCircleOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Hostname"
                          value={metricas.sistema.hostname}
                          prefix={<CloudServerOutlined />}
                        />
                      </Col>
                    </Row>
                  </Space>
                )}
              </Card>
            </Col>

            {/* Resumen de Servicios */}
            <Col xs={24} lg={12}>
              <Card 
                title={<><PlayCircleOutlined /> Servicios PM2</>}
                extra={
                  <Tag color="blue">
                    {servicios.filter(s => s.status === 'online').length} / {servicios.length} Online
                  </Tag>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Online"
                      value={servicios.filter(s => s.status === 'online').length}
                      valueStyle={{ color: '#3f8600' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Stopped"
                      value={servicios.filter(s => s.status === 'stopped').length}
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<StopOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Error"
                      value={servicios.filter(s => s.status === 'errored').length}
                      valueStyle={{ color: '#d46b08' }}
                      prefix={<ExclamationCircleOutlined />}
                    />
                  </Col>
                </Row>

                <Divider />

                <List
                  size="small"
                  dataSource={servicios.slice(0, 5)}
                  renderItem={servicio => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Tag color={servicio.status === 'online' ? 'green' : 'red'}>
                            {servicio.status === 'online' ? 'ON' : 'OFF'}
                          </Tag>
                        }
                        title={servicio.name}
                        description={`CPU: ${servicio.cpu} | RAM: ${servicio.memoria}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Bases de Datos */}
            <Col xs={24}>
              <Card 
                title={<><DatabaseOutlined /> Bases de Datos</>}
                extra={
                  <Tag color="green">
                    {basesDatos.filter(bd => bd.estado === 'conectada').length} Conectadas
                  </Tag>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Bases"
                      value={basesDatos.length}
                      prefix={<DatabaseOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Conexiones Activas"
                      value={basesDatos.reduce((total, bd) => total + bd.conexiones_activas, 0)}
                      prefix={<CloudServerOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Tamaño Total"
                      value={basesDatos.reduce((total, bd) => total + parseFloat(bd.tamaño_mb), 0).toFixed(1)}
                      suffix="MB"
                      prefix={<HddOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Tab de Servicios Detallado */}
        <TabPane tab="Servicios PM2" key="servicios">
          <Card title="Estado Detallado de Servicios">
            <Table
              columns={columnasServicios}
              dataSource={servicios}
              rowKey="id"
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: isMobile ? 'max-content' : undefined }}
              pagination={false}
            />
          </Card>
        </TabPane>

        {/* Tab de Bases de Datos Detallado */}
        <TabPane tab="Bases de Datos" key="bases-datos">
          <Card title="Estado de Bases de Datos">
            <Table
              columns={columnasBD}
              dataSource={basesDatos}
              rowKey="nombre"
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: isMobile ? 'max-content' : undefined }}
              pagination={false}
            />
          </Card>
        </TabPane>

        {/* Tab de Logs */}
        <TabPane tab="Logs del Sistema" key="logs">
          <Card 
            title="Logs Recientes del Sistema"
            extra={
              <Button 
                icon={<BugOutlined />}
                onClick={() => {
                  setServicioSeleccionado({ name: 'Todos los servicios' });
                  setLogsModalVisible(true);
                }}
              >
                Ver Logs Completos
              </Button>
            }
          >
            <Alert
              message="Logs en Tiempo Real"
              description="Los logs se actualizan automáticamente cada 30 segundos"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            {logs.length > 0 ? (
              <List
                size="small"
                dataSource={logs.slice(-10)}
                renderItem={log => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Tag color={
                          log.nivel === 'error' ? 'red' :
                          log.nivel === 'warning' ? 'orange' :
                          log.nivel === 'info' ? 'blue' : 'default'
                        }>
                          {log.nivel.toUpperCase()}
                        </Tag>
                      }
                      title={log.timestamp}
                      description={log.mensaje}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">No hay logs disponibles</Text>
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* Modal de Logs */}
      <Modal
        title={`Logs de ${servicioSeleccionado?.name || 'Sistema'}`}
        visible={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={{ top: isMobile ? 20 : undefined }}
      >
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {logs.length > 0 ? (
            <List
              size="small"
              dataSource={logs}
              renderItem={log => (
                <List.Item style={{ padding: '8px 0' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <Tag color={
                        log.nivel === 'error' ? 'red' :
                        log.nivel === 'warning' ? 'orange' :
                        log.nivel === 'info' ? 'blue' : 'default'
                      }>
                        {log.nivel.toUpperCase()}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {log.timestamp}
                      </Text>
                    </Space>
                    <Text 
                      style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {log.mensaje}
                    </Text>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">No hay logs disponibles para este servicio</Text>
            </div>
          )}
        </div>
      </Modal>

      {/* Footer con información de última actualización */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '16px',
        background: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          🔄 Última actualización: {new Date().toLocaleTimeString()} | 
          ⚡ Auto-refresh: {autoRefresh ? 'Activado (30s)' : 'Desactivado'}
        </Text>
      </div>
    </div>
  );
};

export default Monitoreo;